// AppSync Events API WebSocket subscription client -- see
// specs/open/smartwater-realtime-pipeline-dashboard.md's Approach item 6 for
// why AppSync Events (not GraphQL subscriptions) is used, and
// SmartWaterCDK's EventStreamResources.ts for the API's Cognito
// subscribe-auth configuration.
//
// Protocol confirmed against AWS's own AppSync Events WebSocket protocol
// reference (docs.aws.amazon.com/appsync/latest/eventapi/event-api-websocket-protocol.html),
// not assumed:
//   1. Connect to `wss://<realtime-domain>/event/realtime` with subprotocols
//      `['aws-appsync-event-ws', 'header-<base64url-json>']`.
//   2. Send `{type: 'connection_init'}` and wait for `{type: 'connection_ack'}`.
//   3. Send `{id, type: 'subscribe', channel, authorization}`.
//   4. Events arrive as `{type: 'data', id, event: string[]}` per AWS's own
//      docs, but a real captured frame showed `event` as a bare string
//      (not a single-element array) for a single-event publish -- this
//      client normalizes both shapes rather than trusting the docs alone.
// Cognito auth's `authorization` object is `{ Authorization: <idToken>, host:
// <httpDomain> }` (confirmed field casing -- NOT lowercase `authorization`).
// The realtime domain (`appsync-realtime-api...`) and HTTP domain
// (`appsync-api...`) are genuinely distinct subdomains, not derivable from
// one another -- both come from getDashboardConfig().
import { authService } from '../auth';
import { getDashboardConfig } from '../common/constants';
import { PipelineEventEnvelope } from './interfaces';

function base64UrlEncode(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function buildAuthProtocol(idToken: string, httpDomain: string): string {
  const header = base64UrlEncode(JSON.stringify({ Authorization: idToken, host: httpDomain }));
  return `header-${header}`;
}

// AppSync's WebSocket protocol constrains the subscribe message's "id" to
// /^[a-zA-Z0-9-_+]{1,128}$/ (docs.aws.amazon.com/appsync/latest/eventapi/
// event-api-websocket-protocol.html) -- confirmed the hard way: an earlier
// version of this file built the id by concatenating the channel path
// (which contains slashes) directly, and every subscribe attempt was
// silently rejected with `{"type":"error","errors":[{"errorType":
// "UnsupportedOperation",...}]}` instead of `subscribe_success` -- no
// compile-time or lint signal caught it, only a live WebSocket frame
// capture did. This validating builder makes the constraint structural:
// any future caller gets a thrown error immediately if it ever produces
// an invalid id, instead of a silent protocol-level rejection discovered
// only by inspecting live traffic.
const SUBSCRIPTION_ID_PATTERN = /^[a-zA-Z0-9\-_+]{1,128}$/;

// Date.now() alone is millisecond-resolution and can repeat across two
// calls in the same tick (e.g. a fast league switch triggering two
// subscriptions back to back) -- a module-level counter guarantees
// uniqueness regardless of timing, without needing crypto.randomUUID().
let subscriptionIdCounter = 0;

export function buildSubscriptionId(league: string): string {
  subscriptionIdCounter += 1;
  const id = `pipeline-events-smartwater-${league}-${Date.now()}-${subscriptionIdCounter}`;
  if (!SUBSCRIPTION_ID_PATTERN.test(id)) {
    // Fails loudly at the call site rather than shipping a silently-rejected
    // subscribe message -- e.g. if `league` ever contains a slash or other
    // disallowed character.
    throw new Error(
      `Generated subscription id ${JSON.stringify(id)} does not match AppSync's required ` +
        `pattern ${SUBSCRIPTION_ID_PATTERN}. Check the 'league' value for disallowed characters.`,
    );
  }
  return id;
}

interface AppSyncWsMessage {
  type: string;
  id?: string;
  // AWS's own protocol reference documents `event` as always an array of
  // stringified events (up to 5 per publish batch) -- but a real captured
  // WebSocket frame for a single-event publish showed a bare string
  // instead: `{"type":"data","event":"{\"schema_version\":...}"}`, not
  // `{"event":["{\"schema_version\":...}"]}`. Typed as the union to match
  // what AppSync actually sends, not just what the docs say -- iterating a
  // bare string with `for...of` walks it character-by-character, so
  // treating it as always-an-array silently mis-parses every single-event
  // publish (confirmed via a real `SyntaxError: Unexpected token '}'`).
  event?: string[] | string;
  errors?: unknown;
}

/**
 * Subscribes to one league's pipeline-events channel and invokes `onEvent`
 * for each envelope received. Returns an unsubscribe function that closes
 * the WebSocket -- callers (e.g. a React effect) MUST call this on cleanup,
 * since each subscription opens its own connection.
 *
 * `onError` is invoked for connection failures or malformed messages;
 * `onEvent` is never called with anything but a successfully-parsed
 * envelope, per coding-standards.md's fail-explicit guidance -- a malformed
 * message is reported, not silently dropped or guessed at.
 */
export function subscribeToLeagueEvents(
  league: string,
  onEvent: (event: PipelineEventEnvelope) => void,
  onError: (error: Error) => void,
): () => void {
  const { eventApiHttpDomain, eventApiRealtimeDomain } = getDashboardConfig();
  const idToken = authService.getIdToken();
  if (!idToken) {
    onError(new Error('No ID token available -- user is not signed in.'));
    return () => {};
  }

  const channel = `/pipeline-events/smartwater/${league}`;
  const subscriptionId = buildSubscriptionId(league);

  const socket = new WebSocket(`wss://${eventApiRealtimeDomain}/event/realtime`, [
    'aws-appsync-event-ws',
    buildAuthProtocol(idToken, eventApiHttpDomain),
  ]);

  socket.onopen = () => {
    socket.send(JSON.stringify({ type: 'connection_init' }));
  };

  socket.onmessage = (message: MessageEvent<string>) => {
    let parsed: AppSyncWsMessage;
    try {
      parsed = JSON.parse(message.data);
    } catch (error) {
      onError(new Error(`Malformed AppSync Events message: ${error}`));
      return;
    }

    switch (parsed.type) {
      case 'connection_ack':
        // Only now is the connection ready to register a subscription --
        // sending `subscribe` before this ack is rejected by AppSync.
        socket.send(
          JSON.stringify({
            id: subscriptionId,
            type: 'subscribe',
            channel,
            authorization: { Authorization: idToken, host: eventApiHttpDomain },
          }),
        );
        return;
      case 'ka':
      case 'subscribe_success':
        return; // Keep-alive / subscription confirmed -- no envelope to deliver.
      case 'subscribe_error':
      case 'broadcast_error':
        onError(new Error(`AppSync Events subscription error: ${JSON.stringify(parsed.errors)}`));
        return;
      case 'data': {
        // `event` is documented as an array of stringified events, but a
        // real captured frame showed a bare string for a single-event
        // publish -- normalize to an array before iterating so `for...of`
        // never walks a string character-by-character (see AppSyncWsMessage's
        // `event` field comment for the full incident).
        const rawEvents = Array.isArray(parsed.event) ? parsed.event : parsed.event ? [parsed.event] : [];
        for (const rawEvent of rawEvents) {
          try {
            onEvent(JSON.parse(rawEvent));
          } catch (error) {
            onError(new Error(`Malformed pipeline event envelope: ${error}`));
          }
        }
        return;
      }
      default:
        return; // Unrecognized message type -- ignore rather than fail the whole subscription.
    }
  };

  socket.onerror = () => {
    onError(new Error(`WebSocket connection error for channel ${channel}`));
  };

  // A clean server-side close (e.g. AppSync's connectionTimeoutMs expiring,
  // or the Cognito token expiring server-side) must not fail silently --
  // without this, the dashboard would simply stop receiving events with no
  // visible error. Only report as an error if the client itself didn't
  // initiate the close via the returned unsubscribe function below.
  let closedByClient = false;
  socket.onclose = (event: CloseEvent) => {
    if (!closedByClient) {
      onError(
        new Error(
          `WebSocket closed unexpectedly for channel ${channel} (code ${event.code}): ${event.reason || 'no reason given'}`,
        ),
      );
    }
  };

  return () => {
    closedByClient = true;
    socket.close();
  };
}
